// The ingest orchestrator: file -> kind -> positioned tokens -> lines ->
// Tier-A fields + line items. Scanned PDFs and images route through the
// ON-DEVICE OCR branch (src/ingest/ocr.ts, Tesseract WASM, self-hosted —
// nothing leaves the machine). OCR-sourced reads are marked as such, their
// confidences discounted, and the review UI tells the human to check harder.

import { fileKind, looksScanned } from "./detect";
import { extractTokens } from "./extract";
import { groupLines, linesToText } from "./lines";
import { findInvoiceDate, findInvoiceNo, findMaker, findTotals } from "./fields";
import { extractItems } from "./items";
import type { Field, IngestResult, ParsedInvoice, Token } from "./types";

function buildParsed(tokens: Token[], ocr: boolean): ParsedInvoice {
  const lines = groupLines(tokens);
  const maker = findMaker(lines);
  const invoiceNo = findInvoiceNo(lines);
  const invoiceDate = findInvoiceDate(lines);
  const { total, subtotal } = findTotals(lines);
  const { items, arithmeticOk, warnings } = extractItems(lines, subtotal, total ? total.value : null);

  const parsed: ParsedInvoice = {
    kind: "text",
    maker, invoiceNo, invoiceDate,
    total, subtotal, arithmeticOk,
    items,
    linesText: linesToText(lines),
    warnings,
    ocr,
  };
  return ocr ? markOcr(parsed) : parsed;
}

// OCR reads are honest about their nature: every field is re-labelled as
// OCR-sourced with a discounted confidence, and a standing warning asks the
// human to check the read against the paper.
function markOcr(p: ParsedInvoice): ParsedInvoice {
  const demote = <T>(f: Field<T>): Field<T> => ({ ...f, source: "ocr", confidence: Math.min(f.confidence, 0.9) * 0.95 });
  return {
    ...p,
    maker: demote(p.maker),
    invoiceNo: demote(p.invoiceNo),
    invoiceDate: demote(p.invoiceDate),
    total: p.total ? demote(p.total) : null,
    items: p.items.map(it => ({
      qty: demote(it.qty),
      model: demote(it.model),
      serial: demote(it.serial),
      unitPrice: demote(it.unitPrice),
    })),
    warnings: [
      "Read by on-device OCR from a scanned document. Nothing left this device. Check every field against the paper.",
      ...p.warnings,
    ],
  };
}

export async function ingestFile(fileBuf: ArrayBuffer): Promise<IngestResult> {
  const kind = fileKind(fileBuf);
  if (kind === "unknown") {
    return { kind: "unsupported", reason: "Not a PDF or image. Drop the manufacturer's invoice as PDF, PNG, or JPEG." };
  }

  if (kind === "pdf") {
    // pdf.js TRANSFERS the ArrayBuffer to its worker (detaching it), so the
    // text-layer probe gets a copy and the original stays alive for the OCR path.
    const { tokens, pages, totalChars } = await extractTokens(fileBuf.slice(0));
    if (!looksScanned(totalChars, pages)) return buildParsed(tokens, false);
    // a PDF with no text layer is a scan — fall through to OCR
    return ocrPath(fileBuf, "pdf", pages);
  }

  return ocrPath(fileBuf, "image", 1);
}

async function ocrPath(fileBuf: ArrayBuffer, kind: "pdf" | "image", pages: number): Promise<IngestResult> {
  try {
    const { ocrTokens } = await import("./ocr");
    const tokens = await ocrTokens(fileBuf, kind);
    if (tokens.length < 8) {
      return { kind: "scanned", pages, note: "OCR ran on this device but found almost no readable text. Try a straighter, higher-resolution scan or photo." };
    }
    return buildParsed(tokens, true);
  } catch (err) {
    return { kind: "scanned", pages, note: "On-device OCR could not read this document (" + String(err) + "). Try a clearer scan." };
  }
}
